import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Loader2, Save } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { trpc } from '~/api/trpc';

type StoreSelectionFormData = {
    pageTitleDe: string;
    pageTitleEn: string;
    progressDisplayDe: string;
    progressDisplayEn: string;
    googleRatingValue: number;
    showGoogleRating: boolean;
    loginPromptTextDe: string;
    loginPromptTextEn: string;
    loginPromptLinkTextDe: string;
    loginPromptLinkTextEn: string;
    continueButtonTextDe: string;
    continueButtonTextEn: string;
};

const StoreSelectionCMS = () => {
    const [formData, setFormData] = useState<StoreSelectionFormData>({
        pageTitleDe: '',
        pageTitleEn: '',
        progressDisplayDe: '',
        progressDisplayEn: '',
        googleRatingValue: 4.9,
        showGoogleRating: true,
        loginPromptTextDe: '',
        loginPromptTextEn: '',
        loginPromptLinkTextDe: '',
        loginPromptLinkTextEn: '',
        continueButtonTextDe: '',
        continueButtonTextEn: '',
    });
    const [isDirty, setIsDirty] = useState(false);

    const utils = trpc.useUtils();

    const { data: configData, isLoading: isLoadingConfig } = trpc.bookingFunnelCMS.getStoreSelectionConfig.useQuery();
    const { data: storesData, isLoading: isLoadingStores } = trpc.bookingFunnelCMS.getStoreVisibilities.useQuery();

    const updateConfigMutation = trpc.bookingFunnelCMS.updateStoreSelectionConfig.useMutation({
        onSuccess: () => {
            toast.success('Konfiguration gespeichert');
            setIsDirty(false);
            utils.bookingFunnelCMS.getStoreSelectionConfig.invalidate();
        },
        onError: (error) => {
            toast.error('Fehler beim Speichern', { description: error.message });
        },
    });

    const updateVisibilityMutation = trpc.bookingFunnelCMS.updateStoreVisibility.useMutation({
        onSuccess: () => {
            toast.success('Sichtbarkeit aktualisiert');
            utils.bookingFunnelCMS.getStoreVisibilities.invalidate();
        },
        onError: (error) => {
            toast.error('Fehler beim Aktualisieren', { description: error.message });
        },
    });

    useEffect(() => {
        if (configData?.data) {
            const config = configData.data;
            setFormData({
                pageTitleDe: config.pageTitleDe,
                pageTitleEn: config.pageTitleEn,
                progressDisplayDe: config.progressDisplayDe,
                progressDisplayEn: config.progressDisplayEn,
                googleRatingValue: Number(config.googleRatingValue),
                showGoogleRating: config.showGoogleRating,
                loginPromptTextDe: config.loginPromptTextDe,
                loginPromptTextEn: config.loginPromptTextEn,
                loginPromptLinkTextDe: config.loginPromptLinkTextDe,
                loginPromptLinkTextEn: config.loginPromptLinkTextEn,
                continueButtonTextDe: config.continueButtonTextDe,
                continueButtonTextEn: config.continueButtonTextEn,
            });
        }
    }, [configData]);

    const handleInputChange = (field: keyof StoreSelectionFormData, value: string | number | boolean) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
        setIsDirty(true);
    };

    const handleSave = () => {
        updateConfigMutation.mutate(formData);
    };

    const handleVisibilityToggle = (storeId: number, isVisible: boolean) => {
        updateVisibilityMutation.mutate({ storeId, isVisible });
    };

    if (isLoadingConfig) {
        return (
            <div className='space-y-6'>
                <Skeleton className='h-[400px] w-full' />
                <Skeleton className='h-[300px] w-full' />
            </div>
        );
    }

    return (
        <div className='space-y-6'>
            {/* Copywriting Section */}
            <Card>
                <CardHeader>
                    <CardTitle>Texte & Einstellungen</CardTitle>
                    <CardDescription>
                        Bearbeiten Sie die Texte für die Standort-Auswahl Seite.
                    </CardDescription>
                </CardHeader>
                <CardContent className='space-y-6'>
                    {/* Page Title */}
                    <div className='grid grid-cols-2 gap-4'>
                        <div className='space-y-2'>
                            <Label htmlFor='pageTitleDe'>Seitentitel (Deutsch)</Label>
                            <Input
                                id='pageTitleDe'
                                value={formData.pageTitleDe}
                                onChange={(e) => handleInputChange('pageTitleDe', e.target.value)}
                                placeholder='Standort'
                            />
                        </div>
                        <div className='space-y-2'>
                            <Label htmlFor='pageTitleEn'>Seitentitel (Englisch)</Label>
                            <Input
                                id='pageTitleEn'
                                value={formData.pageTitleEn}
                                onChange={(e) => handleInputChange('pageTitleEn', e.target.value)}
                                placeholder='Location'
                            />
                        </div>
                    </div>

                    {/* Progress Display */}
                    <div className='grid grid-cols-2 gap-4'>
                        <div className='space-y-2'>
                            <Label htmlFor='progressDisplayDe'>Fortschrittsanzeige (Deutsch)</Label>
                            <Input
                                id='progressDisplayDe'
                                value={formData.progressDisplayDe}
                                onChange={(e) => handleInputChange('progressDisplayDe', e.target.value)}
                                placeholder='SCHRITT 1 / 5'
                            />
                        </div>
                        <div className='space-y-2'>
                            <Label htmlFor='progressDisplayEn'>Fortschrittsanzeige (Englisch)</Label>
                            <Input
                                id='progressDisplayEn'
                                value={formData.progressDisplayEn}
                                onChange={(e) => handleInputChange('progressDisplayEn', e.target.value)}
                                placeholder='STEP 1 / 5'
                            />
                        </div>
                    </div>

                    {/* Google Rating */}
                    <div className='grid grid-cols-2 gap-4'>
                        <div className='space-y-2'>
                            <Label htmlFor='googleRatingValue'>Google Bewertung (Wert)</Label>
                            <Input
                                id='googleRatingValue'
                                type='number'
                                step='0.1'
                                min='0'
                                max='5'
                                value={formData.googleRatingValue}
                                onChange={(e) => handleInputChange('googleRatingValue', parseFloat(e.target.value) || 0)}
                            />
                        </div>
                        <div className='flex items-center space-x-2 pt-8'>
                            <Switch
                                id='showGoogleRating'
                                checked={formData.showGoogleRating}
                                onCheckedChange={(checked) => handleInputChange('showGoogleRating', checked)}
                            />
                            <Label htmlFor='showGoogleRating'>Google Bewertung anzeigen</Label>
                        </div>
                    </div>

                    {/* Login Prompt Text */}
                    <div className='grid grid-cols-2 gap-4'>
                        <div className='space-y-2'>
                            <Label htmlFor='loginPromptTextDe'>Login-Hinweis Text (Deutsch)</Label>
                            <Input
                                id='loginPromptTextDe'
                                value={formData.loginPromptTextDe}
                                onChange={(e) => handleInputChange('loginPromptTextDe', e.target.value)}
                                placeholder='Du warst schon mal bei uns?'
                            />
                        </div>
                        <div className='space-y-2'>
                            <Label htmlFor='loginPromptTextEn'>Login-Hinweis Text (Englisch)</Label>
                            <Input
                                id='loginPromptTextEn'
                                value={formData.loginPromptTextEn}
                                onChange={(e) => handleInputChange('loginPromptTextEn', e.target.value)}
                                placeholder='Have you been here before?'
                            />
                        </div>
                    </div>

                    {/* Login Prompt Link Text */}
                    <div className='grid grid-cols-2 gap-4'>
                        <div className='space-y-2'>
                            <Label htmlFor='loginPromptLinkTextDe'>Login-Link Text (Deutsch)</Label>
                            <Input
                                id='loginPromptLinkTextDe'
                                value={formData.loginPromptLinkTextDe}
                                onChange={(e) => handleInputChange('loginPromptLinkTextDe', e.target.value)}
                                placeholder='Klicke hier um dich einzuloggen.'
                            />
                        </div>
                        <div className='space-y-2'>
                            <Label htmlFor='loginPromptLinkTextEn'>Login-Link Text (Englisch)</Label>
                            <Input
                                id='loginPromptLinkTextEn'
                                value={formData.loginPromptLinkTextEn}
                                onChange={(e) => handleInputChange('loginPromptLinkTextEn', e.target.value)}
                                placeholder='Click here to login.'
                            />
                        </div>
                    </div>

                    {/* Continue Button */}
                    <div className='grid grid-cols-2 gap-4'>
                        <div className='space-y-2'>
                            <Label htmlFor='continueButtonTextDe'>Weiter-Button (Deutsch)</Label>
                            <Input
                                id='continueButtonTextDe'
                                value={formData.continueButtonTextDe}
                                onChange={(e) => handleInputChange('continueButtonTextDe', e.target.value)}
                                placeholder='WEITER'
                            />
                        </div>
                        <div className='space-y-2'>
                            <Label htmlFor='continueButtonTextEn'>Weiter-Button (Englisch)</Label>
                            <Input
                                id='continueButtonTextEn'
                                value={formData.continueButtonTextEn}
                                onChange={(e) => handleInputChange('continueButtonTextEn', e.target.value)}
                                placeholder='CONTINUE'
                            />
                        </div>
                    </div>

                    {/* Save Button */}
                    <div className='flex justify-end pt-4'>
                        <Button onClick={handleSave} disabled={!isDirty || updateConfigMutation.isPending}>
                            {updateConfigMutation.isPending ? (
                                <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                            ) : (
                                <Save className='mr-2 h-4 w-4' />
                            )}
                            Speichern
                        </Button>
                    </div>
                </CardContent>
            </Card>

            {/* Store Visibility Section */}
            <Card>
                <CardHeader>
                    <CardTitle>Standort-Sichtbarkeit</CardTitle>
                    <CardDescription>
                        Legen Sie fest, welche Standorte im Buchungs-Funnel angezeigt werden sollen.
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    {isLoadingStores ? (
                        <Skeleton className='h-[200px] w-full' />
                    ) : (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Standort</TableHead>
                                    <TableHead>Stadt</TableHead>
                                    <TableHead className='w-[100px] text-center'>Sichtbar</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {storesData?.data.map((store) => (
                                    <TableRow key={store.id}>
                                        <TableCell className='font-medium'>{store.name}</TableCell>
                                        <TableCell>{store.city}</TableCell>
                                        <TableCell className='text-center'>
                                            <Switch
                                                checked={store.isVisible}
                                                onCheckedChange={(checked) => handleVisibilityToggle(store.id, checked)}
                                                disabled={updateVisibilityMutation.isPending}
                                            />
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>
        </div>
    );
};

export default StoreSelectionCMS;
